import { useRecoilValue, useSetRecoilState } from "recoil"

import { isBuyableProduct } from "../helpers/buyableProducts"
import { planItemFor } from "../helpers/productKeys"
import { PlanType, ProductName } from "../interfaces"
import { isPlanSelectionEnabled } from "../lib/featureFlags"
import useCurrentService from "../recoils/atoms/currentService"
import useCurrentTeam from "../recoils/atoms/currentTeam"
import usePlanPurchaseModal from "../recoils/atoms/planPurchaseModal"
import usePlans from "../recoils/atoms/plans"
import usePurchaseConfirmModal from "../recoils/atoms/purchaseConfirmModal"

export const usePurchaseModal = () => {
  const currentService = useRecoilValue(useCurrentService)
  const currentTeam = useRecoilValue(useCurrentTeam)
  const plans = useRecoilValue(usePlans)
  const setPlanPurchaseModal = useSetRecoilState(usePlanPurchaseModal)
  const setPurchaseConfirmModal = useSetRecoilState(usePurchaseConfirmModal)

  const openPlanPurchaseModal = (planType: PlanType) => {
    // pages/Plan.tsx の ?plan_type= はプラン一覧を経由せずにここへ来る。一覧と同じ
    // 条件で止めないと、公開前の CLOUD でも URL を直接開けば申し込める（#400）。
    if (!isPlanSelectionEnabled(currentService.name)) return

    const plan = plans.list.find((p) => p.planType === planType)
    if (!plan) return

    if (currentTeam.planTypes[currentService.name] === planType) return

    setPlanPurchaseModal({ plan: plan })
    setPurchaseConfirmModal({
      isVisible: true,
      planType: planType,
      productName: null,
      price: plan.price,
    })
  }

  const openSinglePurchaseModal = (productName: ProductName) => {
    const plan = plans.list.find(
      (p) => p.planType === currentTeam.planTypes[currentService.name],
    )
    if (!plan) return

    // PlanContent と同じ取り違えがここにもあった（#410）。CLOUD の
    // custom_domain / build_time を単体購入できるようにした瞬間、
    // `as keyof PlanItems` は typecheck を通したまま undefined を返す。
    const item = planItemFor(plan.items, productName)
    if (!item) return

    // 単体購入できる商品の表は helpers/buyableProducts.ts が正本。
    // SinglePurchases.tsx も同じ表からセルを描くので、**描けるものと開けるものが
    // ずれません**（以前はずれていて、CLOUD 側にガードが無かった / #415）。
    if (!isBuyableProduct(currentService.name, productName)) return

    setPurchaseConfirmModal({
      isVisible: true,
      planType: null,
      productName: productName,
      price: item.price,
    })
  }

  return {
    openPlanPurchaseModal,
    openSinglePurchaseModal,
  }
}
